import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, Crosshair, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { API_BASE } from "@/lib/api";

/** A region center (a whole state/country) is matched exactly server-side, not by radius. */
export const isRegionCenter = (c) =>
  !!c && (((c.granularity === "state") && !!c.state_code) || ((c.granularity === "country") && !!c.country_code));


/**
 * Compact "miles within" geo-radius control for horizontal filter bars.
 * value: { geoOn: bool, center: {lat,lng,label}|null, radius: number }
 * onChange(patch): merges a partial update into value.
 */
export const GeoRadiusFilter = ({ value, onChange, testId = "geo" }) => {
  const [open, setOpen] = useState(false);
  const [locInput, setLocInput] = useState("");
  const [busy, setBusy] = useState(false);
  const active = value.geoOn && value.center;

  const lookup = async () => {
    if (!locInput.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/directory/geo?q=${encodeURIComponent(locInput.trim())}`, { credentials: "include" });
      if (!r.ok) throw new Error("not found");
      const d = await r.json();
      onChange({ geoOn: true, center: {
        lat: d.lat, lng: d.lng, label: d.formatted || locInput.trim(),
        granularity: d.granularity, state_code: d.state_code, country_code: d.country_code,
      } });
    } catch (e) {
      toast.error("Couldn't find that location. Try a city, state, province, or ZIP.");
    } finally {
      setBusy(false);
    }
  };

  const locateMe = () => {
    if (!navigator.geolocation) { toast.error("Geolocation isn't supported on this device."); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({ geoOn: true, center: { lat: pos.coords.latitude, lng: pos.coords.longitude, label: "My location" } });
        setBusy(false);
      },
      () => { toast.error("Couldn't access your location."); setBusy(false); },
      { timeout: 8000 }
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={active ? "default" : "secondary"}
          className="gap-1.5"
          data-testid={`${testId}-trigger`}
        >
          <MapPin className="h-4 w-4" />
          <span className="max-w-[170px] truncate">{active ? (isRegionCenter(value.center) ? value.center.label : `Within ${value.radius} mi · ${value.center.label}`) : "Distance"}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" data-testid={`${testId}-panel`}>
        <div className="flex items-center justify-between">
          <Label htmlFor={`${testId}-switch`} className="text-sm font-[560]">Search by distance</Label>
          <Switch id={`${testId}-switch`} checked={!!value.geoOn} onCheckedChange={(v) => onChange({ geoOn: v })} data-testid={`${testId}-toggle`} />
        </div>

        {value.geoOn && (
          <div className="mt-3 space-y-3">
            <div className="flex gap-2">
              <Input
                value={locInput}
                onChange={(e) => setLocInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookup(); } }}
                placeholder="City, state, province, or ZIP"
                data-testid={`${testId}-input`}
              />
              <Button variant="secondary" size="icon" onClick={locateMe} disabled={busy} title="Use my location" data-testid={`${testId}-locate`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
              </Button>
            </div>

            {value.center && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="truncate">{value.center.label}</span>
                <button onClick={() => onChange({ center: null })} className="ml-auto text-muted-foreground hover:text-foreground" title="Clear location"><X className="h-3.5 w-3.5" /></button>
              </div>
            )}

            {isRegionCenter(value.center) ? (
              <p className="text-xs text-muted-foreground" data-testid={`${testId}-region-note`}>
                Showing everyone across <span className="font-[600] text-foreground">{value.center.label}</span>. The distance radius doesn't apply to a whole state or country.
              </p>
            ) : (
              <div>
                <div className="mb-1.5 flex items-center justify-between text-xs">
                  <span className="font-[600] text-muted-foreground">Within</span>
                  <span className="font-[640] text-foreground" data-testid={`${testId}-radius-value`}>{value.radius} mi</span>
                </div>
                <Slider min={5} max={500} step={5} value={[value.radius]} onValueChange={(v) => onChange({ radius: v[0] })} data-testid={`${testId}-radius-slider`} />
              </div>
            )}

            {!value.center && (
              <p className="text-xs text-muted-foreground">Enter a location or use your current location to filter by distance.</p>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
